/**
 * How league-core names Pokémon in its messages and finds them from what people type. Showdown's draft plugin
 * plugs in Showdown's own Dex (with its aliases); the web app and the API use a pokemon-core snapshot.
 */
import { toID, type PokemonData } from '@ogreclan/pokemon-core';

export interface ResolvedSpecies {
	id: string;
	name: string;
	/** The base species' id: "charizard" for Charizard-Mega-Y */
	baseId: string;
}

export interface SpeciesNames {
	/** A Pokémon's name from its id */
	name(id: string): string;
	/** A Pokémon from a name someone typed, or null if there's no such Pokémon */
	resolve(name: string): ResolvedSpecies | null;
}

let names: SpeciesNames = {
	name: id => id,
	resolve: name => toID(name) ? { id: toID(name), name: name.trim(), baseId: toID(name) } : null,
};

export function setSpeciesNames(provider: SpeciesNames) {
	names = provider;
}

export function speciesName(id: string) {
	return names.name(id) || id;
}

export function resolveSpecies(name: string) {
	return names.resolve(name);
}

/** Species names from a pokemon-core data snapshot */
export function speciesNamesFromData(data: PokemonData): SpeciesNames {
	return {
		name: id => data.getSpecies(id)?.name ?? id,
		resolve(name) {
			const species = data.getSpecies(name);
			return species ? { id: species.id, name: species.name, baseId: species.baseSpecies } : null;
		},
	};
}
